/**
 * Hover zoom with cursor tracking for the product gallery (desktop only).
 *
 * Lightweight, dependency-free enhancement: on pointer devices, hovering a
 * gallery image scales it and moves the transform-origin to follow the cursor
 * (a "magnifier / seguimiento" effect). It uses a single set of delegated
 * document listeners, so it keeps working after variant re-renders replace the
 * gallery node, and never re-initialises.
 *
 * Performance notes:
 *   - Layout reads (getBoundingClientRect) and style writes happen inside a
 *     single requestAnimationFrame callback, so pointer moves, scroll and resize
 *     never force a synchronous reflow — at most one read + one write per frame.
 *     The only exception is the first measurement on hover-in, which must be
 *     synchronous (see `start`).
 *   - transform-origin writes are skipped when the value is unchanged.
 *   - Pointer coordinates are kept in scalars, so the high-frequency move
 *     handler allocates nothing.
 *   - Uses transform/transform-origin only (GPU-composited, no extra requests).
 */
(function () {
  // Only enable where a real pointer can hover (skips touch / coarse pointers).
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

  var MIN_WIDTH = 1000; // Desktop breakpoint used by the gallery layout.

  var media = null; // Currently hovered .product-gallery__media
  var img = null; // Its <img>
  var rect = null; // Cached bounding box of `img`
  var pointerX = 0; // Last pointer position awaiting a frame
  var pointerY = 0;
  var raf = 0;
  var rectDirty = true; // Recompute `rect` on the next frame
  var lastOrigin = ''; // Last written transform-origin (skip redundant writes)

  function schedule() {
    if (!raf) raf = requestAnimationFrame(frame);
  }

  /**
   * Measures the *image*, not the slide.
   *
   * With "Mostrar completa" (object-fit: contain) the <img> is sized by its own
   * aspect ratio and centred inside the slide, so the two boxes differ. Since
   * transform-origin percentages resolve against the element they are written
   * to, measuring the slide would offset the tracking by the size of the empty
   * bands. Measuring the image keeps both modes exact.
   *
   * Returns false when the element is gone or has no box — the caller then
   * tears everything down instead of dividing by zero.
   */
  function measure() {
    if (!img.isConnected) return false;
    rect = img.getBoundingClientRect();
    rectDirty = false;
    return rect.width > 0 && rect.height > 0;
  }

  function writeOrigin() {
    var x = ((pointerX - rect.left) / rect.width) * 100;
    var y = ((pointerY - rect.top) / rect.height) * 100;

    // The slide can be larger than the image (contain), so the cursor may sit
    // outside the image box while still hovering the slide. Clamping keeps the
    // origin on the image instead of letting it drift off-picture.
    if (x < 0) x = 0;
    else if (x > 100) x = 100;
    if (y < 0) y = 0;
    else if (y > 100) y = 100;

    var origin = x + '% ' + y + '%';
    if (origin !== lastOrigin) {
      img.style.transformOrigin = origin;
      lastOrigin = origin;
    }
  }

  function frame() {
    raf = 0;
    if (!img) return;
    // A variant re-render can replace the gallery while the pointer is still
    // inside it; the slide's own listener dies with the node but this one may
    // still fire from scroll/resize. Bail out and clean up.
    if (rectDirty && !measure()) {
      stop();
      return;
    }
    writeOrigin();
  }

  function onMove(event) {
    pointerX = event.clientX;
    pointerY = event.clientY;
    schedule();
  }

  // Scroll/resize only invalidate the cached rect; the recompute is deferred to
  // the animation frame to avoid layout thrashing.
  function onViewportChange() {
    rectDirty = true;
    schedule();
  }

  function stop() {
    if (!media) return;
    media.removeEventListener('pointermove', onMove);
    window.removeEventListener('scroll', onViewportChange);
    window.removeEventListener('resize', onViewportChange);
    media.classList.remove('is-hover-zooming');
    img.style.transform = '';
    img.style.transformOrigin = '';
    if (raf) cancelAnimationFrame(raf);
    media = img = rect = null;
    raf = 0;
    rectDirty = true;
    lastOrigin = '';
  }

  function start(target, event) {
    var gallery = target.closest('product-gallery[hover-zoom]');
    if (!gallery) return;

    var image = target.querySelector('img');
    if (!image) return;

    // scale(1) is a visual no-op: skip the listeners and the class entirely
    // rather than run a per-frame loop that changes nothing.
    var level = parseFloat(gallery.getAttribute('hover-zoom')) || 2;
    if (!(level > 1)) return;

    media = target;
    img = image;
    pointerX = event.clientX;
    pointerY = event.clientY;
    lastOrigin = '';

    // Measure and set the origin BEFORE applying the scale, in this same task.
    // The CSS transition then eases out from the point under the cursor; doing
    // it the other way round starts the animation from the default centre and
    // snaps to the cursor one frame later, which reads as a jump when entering
    // through an edge. This is the one synchronous layout read per hover-in.
    if (!measure()) {
      stop();
      return;
    }
    writeOrigin();

    target.classList.add('is-hover-zooming');
    img.style.transform = 'scale(' + level + ')';

    target.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('scroll', onViewportChange, { passive: true });
    window.addEventListener('resize', onViewportChange, { passive: true });
  }

  document.addEventListener(
    'pointerover',
    function (event) {
      // Cheap guards first: this fires for every element the pointer enters
      // anywhere on the page, and closest() walks the tree.
      if (window.innerWidth < MIN_WIDTH || !event.target.closest) return;

      var target = event.target.closest('.product-gallery__media[data-media-type="image"]');
      if (target && target !== media) {
        stop();
        start(target, event);
      }
    },
    { passive: true }
  );

  document.addEventListener(
    'pointerout',
    function (event) {
      // Ignore moves onto children; only stop when the pointer truly leaves.
      if (media && !media.contains(event.relatedTarget)) stop();
    },
    { passive: true }
  );
})();
